import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useDispatch } from 'react-redux';
import { Button, Grid } from '@mui/material';

import {
	BooleanDescField,
	EditorHtmlField,
	Form,
	ResponsiveDialog,
	SimpleTextField,
	useModel,
	useSettings,
	Utils
} from 'app/main/components';
import { type WbsRootNode } from './useWbsState';

interface WbsModelsSaveDialogProps {
	open: boolean;
	onClose: VoidFunction;
	root: WbsRootNode;
}

export default function WbsModelsSaveDialog({ open, onClose, root }: WbsModelsSaveDialogProps) {
	const dispatch = useDispatch();
	const { settings, t } = useSettings('wbs_models');

	const { model, sendChange, save } = useModel({
		baseModel: {
			wbsm_uid: uuidv4(),
			wbsm_parent_table: root.table,
			wbsm_parent_table_uid: root.tableId
		},
		settings,
		isEdit: true,
		isAdd: true
	});

	const sendData = () => {
		save({
			onSuccess: () => {
				dispatch(Utils.alertSuccessMsg(t));
				onClose();
			}
		});
	};

	return (
		<ResponsiveDialog
			open={open}
			onClose={onClose}
			maxWidth="sm"
			fullWidth
			title={t('WBS_DIALOG_ACTION_SAVE_MODEL')}
			content={
				<Form id="wbsModelsSaveDialog" onSubmit={sendData}>
					<Grid container spacing={1}>
						<Grid size={12}>
							{/* TITLE */}
							<SimpleTextField
								name="wbsm_title"
								label={t('DETAILS_WBS_MODELS_FIELD_TITLE')}
								value={model.wbsm_title}
								isEdit
								sendChange={sendChange}
								required
								validationError={t('ERROR_TEXT_REQUIRED')}
							/>
						</Grid>
						<Grid size={12}>
							{/* DESCRIPTION */}
							<EditorHtmlField
								name="wbsm_descriptionhtml"
								label={t('DETAILS_WBS_MODELS_FIELD_DESCRIPTION')}
								value={model.wbsm_descriptionhtml}
								isEdit
								sendChange={sendChange}
							/>
						</Grid>
						<Grid size={{ sm: 6, xs: 12 }}>
							{/* WARNING */}
							<BooleanDescField
								name="wbsm_warning_desc"
								label={t('DETAILS_WBS_MODELS_FIELD_WARNING')}
								value={model.wbsm_warning_desc}
								icon="alert-outline"
								color="warning.main"
								isEdit
								sendChange={sendChange}
							/>
						</Grid>
						<Grid size={{ sm: 6, xs: 12 }}>
							{/* BLOCKED */}
							<BooleanDescField
								name="wbsm_blocked_desc"
								label={t('DETAILS_WBS_MODELS_FIELD_BLOCKED')}
								value={model.wbsm_blocked_desc}
								icon="cancel"
								color="error.main"
								isEdit
								sendChange={sendChange}
							/>
						</Grid>
					</Grid>
				</Form>
			}
			actions={
				<>
					<Button type="submit" form="wbsModelsSaveDialog">
						{t('DIALOG_ACTION_SAVE')}
					</Button>
					<Button onClick={onClose}>{t('DIALOG_ACTION_CANCEL')}</Button>
				</>
			}
		/>
	);
}
